import { canOpenTemplatePopup, esignTemplatesFor, ESIGN_ROLE_MESSAGE, ESIGN_ROLE_POINTER } from "./esignTemplates"
import React, { useEffect, useState } from 'react'
import {
  DataGrid,
  useGridApiRef
} from '@mui/x-data-grid'
import { styled } from '@mui/material/styles'
import notFound from "../../../containers/Contacts/Table/NotFoundData"
import { useMyContext } from "../../MyContextProvider/MyContextProvider"
import { getSignDocuments, getTemplates, resendSignDocument } from "../../../features/documents/documents"
import { useDispatch, useSelector } from "react-redux"
import { selectFeatureFlag } from "../../../features/featureFlags/selectors"
import { REQUEST_ERRORS_SMS } from "../../../constants/requestErrorFlags"
import { ESIGN_PENDING_LINK_EXPIRY } from "../../../config/featureFlagKeys"
import { formatDate, getSlashDateFormat } from "../../../utils/date"
import { signerIpLabel, uploadedSignDocumentLabel } from "../../../utils/ip"
import { describeUserAgent } from "../../../utils/userAgent"
import Button from "../../Button/Button"
import ConfirmModal from "../../ConfirmModal/ConfirmModal"
import { useMyContextLoading } from "../../LoadingContextProvider/LoadingContextProvider"
import { showSnackBar } from "../../Snackbar/Snackbar"
import { useIsSupervisor } from "../../../hooks/user"
import { TABLE_CARD_INNER } from "../../Table/tableCardClass"


// Local styled component removed in favor of global theme overrides

const signedSignerLines = (row, toText) => (
  <div>
    {(row?.signers || []).filter((signer) => signer?.signed).map((signer, index) => (
      <div key={index}>{toText(signer)}</div>
    ))}
  </div>
)

const evidenceColumns = (uploadedTypeId) => [
  {
    field: 'signerIp',
    headerName: 'IP',
    sortable: false,
    renderCell: (params) => uploadedSignDocumentLabel(params?.row, uploadedTypeId) ?? signedSignerLines(params?.row, signerIpLabel),
    flex: 1
  },
  {
    field: 'signerDevice',
    headerName: 'Device',
    sortable: false,
    renderCell: (params) => signedSignerLines(params?.row, (signer) => describeUserAgent(signer?.userAgent) || 'Not recorded'),
    flex: 1
  },
  {
    field: 'signerSignedAt',
    headerName: 'Signed at',
    sortable: false,
    renderCell: (params) => signedSignerLines(params?.row, (signer) => (signer?.signedAt ? formatDate(signer.signedAt) : 'N/A')),
    flex: 1
  },
]

const DocsEsignTabTable = ({ userId, userRole, showEvidence = false, uploadedTypeId = null, signingLinkOn = false, signingLinkOffered = false, pcaForAffiliates = false }) => {
  const apiRef = useGridApiRef()
  const dispatch = useDispatch()
  const smsStrict = useSelector(selectFeatureFlag(REQUEST_ERRORS_SMS))
  // WS 2312369: with unsigned links expiring, Resend must reach every signer, SMS ones included.
  const resendAnySigner = useSelector(selectFeatureFlag(ESIGN_PENDING_LINK_EXPIRY))

  const { data, updateData } = useMyContext()
  const [dataSignDocs, setUpdateDataSignDocs] = useState([])
  const [openResetModal, setOpenResetModal] = useState({ open: false, docId: '' })
  const { triggerLoading } = useMyContextLoading()
  const isSupervisor = useIsSupervisor()


  const handleClick = (typeDoc) => {
    if (signingLinkOn && !canOpenTemplatePopup({ userRole, isSupervisor, typeDoc, pcaForAffiliates })) {
      showSnackBar("error", signingLinkOffered ? `${ESIGN_ROLE_MESSAGE} ${ESIGN_ROLE_POINTER}` : ESIGN_ROLE_MESSAGE)
      return
    }
    getTemplateDoc(typeDoc)
  }

  const getTemplateDoc = async (typeDoc) => {
    try {
      const response = await dispatch(getTemplates({ userId, typeDoc }))
      if (response?.error) {
        showSnackBar("error", response?.payload || "Failed to load document template")
        return
      }
      const data = response?.payload?.data
      if (data && Object.keys(data).length > 0) {
        updateData({ status: true, view: 'show', data, typeDoc })
      } else {
        showSnackBar("error", "No template data returned")
      }
    } catch (error) {
      console.error('getTemplateDoc error:', error)
      showSnackBar("error", error?.message || "Failed to load document template")
    }
  }


  const getSignDoc = async (typeDoc) => {
    triggerLoading(true)
    const response = await dispatch(getSignDocuments({ userId, typeDoc }))
    if (response?.payload?.data?.docs && response?.payload?.data?.docs?.length > 0) {
      let updData = []
      response.payload.data.docs.forEach((item) => {
        const newDocs = {
          id: item?._id,
          dateUser: { date: item?.updatedAt, user: item?.user },
          ...item
        }
        updData.push({ ...newDocs })
      })
      setUpdateDataSignDocs(updData)
    } else {
      console.log('No payment data available.')
    }
    triggerLoading(false)
  }
  const resendDoc = async () => {
    triggerLoading(true)
    if (openResetModal?.docId?.length > 0) {
      const docId = openResetModal?.docId
      const email = openResetModal?.email
      const response = await dispatch(resendSignDocument(email ? { docId, email } : { docId }))
      if (response?.payload?.data[0]) showSnackBar("success", email ? "The email has been resend" : "The text message has been sent")
      if (smsStrict && response?.payload?.smsError) showSnackBar("alert", `The text message was not sent: ${response.payload.smsError}`)
    }
    triggerLoading(false)
  }


  useEffect(() => {
    const fetchData = async () => {
      setUpdateDataSignDocs([])
      await getSignDoc()
    }
    fetchData()
  }, [])

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      sortable: false,
      flex: 1
    },
    {
      field: 'form_name',
      headerName: 'Form Name',
      sortable: false,
      flex: 1
    }, {
      field: 'description',
      headerName: 'Description',
      sortable: false,
      flex: 1
    },
    {
      field: 'action',
      headerName: 'Action(s)',
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        return (<Button onClick={() => handleClick(params?.row?.id)} size="sm" width="" classes="font-sans !font-bold">
          Use Document</Button>)
      },
    }
  ]
  const columnsStatus = [
    {
      field: 'id',
      headerName: 'ID',
      sortable: false,
      flex: 1
    },
    {
      field: 'sent',
      headerName: 'Sent',
      renderCell: (params) => {
        return (
          <div>
            <div>{params.row?.creator?.firstName} {params?.row?.creator?.lastName}</div>
            <div className="text-text-secondary">{getSlashDateFormat(params?.row?.dateUser?.date)}</div>
          </div>
        )
      },
      sortable: false,
      flex: 1
    },
    {
      field: 'email',
      headerName: 'Email',
      sortable: false,
      renderCell: (params) => {
        return (
          <div>
            {params?.row?.signers.map((signer, index) => (
              !signer.signed ? (
                <div key={index}>
                  {signer?.email}
                </div>
              ) : null
            ))}
          </div>
        )
      },
      flex: 1
    },
    {
      field: 'status',
      headerName: 'Status',
      renderCell: (params) => {
        return (
          params?.row?.status === "signed" ?
            <div
              className=" font-fontPublic font-bold bg-warning-light w-[63px] h-[28px] label flex justify-center items-center gap-1.5 py-[3px] px-2 rounded-md text-[12px] text-warning-main text-center"
            >
              Signed
            </div> :
            <div
              className=" font-fontPublic font-bold bg-warning-light w-[63px] h-[28px] label flex justify-center items-center gap-1.5 py-[3px] px-2 rounded-md text-[12px] text-warning-main text-center"
            >
              Pending
            </div>
        )
      },
      sortable: false,
      flex: 1
    },
    {
      field: 'action',
      headerName: 'Action',
      sortable: false,
      renderCell: (params) => {
        if (showEvidence && params?.row?.status === 'signed') return null
        return (
          <div
            className=" font-fontPublic cursor-pointer text-md px-[10px] py-[3px] max-w-max  rounded-md font-semibold text-primary-main enabled:hover:bg-primary-main/10 enabled:hover:border-primary-main disabled:bg-surface disabled:border-surface disabled:text-text-secondary border !border-primary-main"
            onClick={() => {
              // On: whoever may send this document may resend it, and a signer with no email is resent by
              // SMS (the server resends on the channel the document went out on).
              if (resendAnySigner) {
                const unsignedSigner = params?.row?.signers?.find(s => !s.signed)
                if (unsignedSigner && canOpenTemplatePopup({ userRole, isSupervisor, typeDoc: params?.row?.typeDoc, pcaForAffiliates })) {
                  setOpenResetModal({ docId: params?.row?._id, email: unsignedSigner.email, open: true })
                }
                return
              }
              if (userRole === 'customer' || isSupervisor) {
                const unsignedSigner = params?.row?.signers?.find(s => !s.signed)
                if (unsignedSigner?.email) {
                  setOpenResetModal({
                    docId: params?.row?._id,
                    email: unsignedSigner.email,
                    open: true
                  })
                }
              }
            }}>
            Resend
          </div>
        )
      },
      flex: 1
    }
  ]
  const statusGridColumns = showEvidence
    ? [...columnsStatus.slice(0, -1), ...evidenceColumns(uploadedTypeId), columnsStatus[columnsStatus.length - 1]]
    : columnsStatus

  return (
    <div className="px-4">
      <ConfirmModal title="Are you sure to Resend this document?"
        functionButton={resendDoc}
        modalButtonTitle={"Resend"}
        open={openResetModal?.open} setOpen={(v) => setOpenResetModal({ ...openResetModal, 'open': v })}
        titleClasses="text-left ml-0"
      >
        You will not be able to cancel this action
      </ConfirmModal>

      <div>
        <div className={`${TABLE_CARD_INNER} w-full`}>
          <DataGrid
            sx={{
              border: 'none',
              backgroundColor: 'background.paper',
              '& .MuiDataGrid-columnHeaders': {
                '.MuiButtonBase-root[aria-haspopup="menu"]': {
                  display: 'none'
                }
              }
            }}
            rowHeight={75}
            columns={columns}
            slots={{
              noRowsOverlay: notFound,
            }}
            columnVisibilityModel={{
              id: false
            }}
            rows={esignTemplatesFor(userRole)}
            apiRef={apiRef}

            hideFooterPagination={true}

            disableRowSelectionOnClick
            disableColumnSelector
          />
        </div>
      </div>
      <div className={`${TABLE_CARD_INNER} mt-6`}>
        <DataGrid
          sx={{
            border: 'none',
            backgroundColor: 'background.paper'
          }}
          rowHeight={75}
          columns={statusGridColumns}
          slots={{
            noRowsOverlay: notFound,
          }}
          columnVisibilityModel={{
            id: false
          }}
          rows={dataSignDocs}
          apiRef={apiRef}

          hideFooterPagination={true}

          disableRowSelectionOnClick
          disableColumnSelector
        />
      </div>
    </div>
  )
}

export default DocsEsignTabTable
